import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Tag, Button, Space, Drawer, Form, Select, Input, message, Tooltip, Alert, DatePicker
} from 'antd';
import { RocketOutlined, EyeOutlined, AppstoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ErpDataGrid, ErpMasterShell, useErpMasterQuery } from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';
import { workOrdersApi } from './workOrdersApi';
import WorkOrderStagesModal from './WorkOrderStagesModal';
import { proformaApi } from '../../sales/proforma/proformaApi';
import {
  WO_STATUS_TABS, statusForTab, woStatusLabel, woStatusColor,
  canViewWorkOrders, canReleaseWorkOrder,
  formatSqm, isCritical
} from './workOrderHelpers';
import '../production.css';

// ════════════════════════════════════════════════════════════════════
// Stage WO-1A — Work Orders · Production Tracking.
//
// Reworked to mirror the shop's existing "Order Tracking" board: a Customer /
// WONO / PI-No / Status filter row and a grouped-header tracking grid —
// Thick · Type · | Pcs · Sqm | · per-department C P F W T RD D · Days · %. The
// department columns show each order's ROUTED workload per department today;
// completed-at-department counts layer on once the WO-1B scan engine lands.
// "Release to Production" turns a CONFIRMED proforma (1:1) into a piece-tracked,
// barcoded work order. (KPI summary cards deferred — added later.)
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
function normList(data) {
  if (Array.isArray(data)) return data;
  return (data && Array.isArray(data.rows)) ? data.rows : [];
}
function fmtDate(v) {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
}
function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function WorkOrdersPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const permissions = me && me.permissions;
  const mayRelease = canReleaseWorkOrder(permissions);
  const mayView = canViewWorkOrders(permissions);

  // ── Server-mode data (paginated + searchable) ────────────────────────
  // Scales to 10k–100k+ WOs: the backend paginates, filters, and searches;
  // the grid only ever holds one page. Filters flow through the query state.
  const [tab, setTab] = useState('all');
  const [customer, setCustomer] = useState('');
  const [wono, setWono] = useState('');
  const [pino, setPino] = useState('');

  const adapter = useMemo(() => ({
    list: (q) => workOrdersApi.list({
      page: q.page,
      page_size: q.pageSize,
      status: statusForTab(q.status || 'all'),
      customer: (q.customer || '').trim() || undefined,
      wono: (q.wono || '').trim() || undefined,
      pino: (q.pino || '').trim() || undefined
    }).then((data) => ({
      rows: normList(data),
      total: (data && data.total) || 0,
      meta: { stageColumns: (data && data.stageColumns) || [] }
    }))
  }), []);

  const onListError = useCallback((err) => message.error(backendError(err, 'Could not load work orders.')), []);
  const { gridHandlers, patchQuery, refetch, meta, loading } = useErpMasterQuery({
    adapter, pageSize: 50, onError: onListError
  });
  const stageColumns = useMemo(() => (meta && meta.stageColumns) || [], [meta]);
  const refresh = refetch;

  // Debounce the three text boxes into the server query (skip the mount run so
  // the initial page load isn't fired twice).
  const filtersMounted = useRef(false);
  useEffect(() => {
    if (!filtersMounted.current) { filtersMounted.current = true; return; }
    const t = setTimeout(() => patchQuery({ customer, wono, pino, page: 1 }), 300);
    return () => clearTimeout(t);
  }, [customer, wono, pino, patchQuery]);

  // Status tab → server query (immediate; skip the mount run).
  const tabMounted = useRef(false);
  useEffect(() => {
    if (!tabMounted.current) { tabMounted.current = true; return; }
    patchQuery({ status: tab, page: 1 });
  }, [tab, patchQuery]);

  // Release drawer.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [confirmedLoading, setConfirmedLoading] = useState(false);
  const [confirmed, setConfirmed] = useState([]);
  const [form] = Form.useForm();

  // Release drawer — CONFIRMED proformas eligible for release.
  const loadConfirmed = useCallback(async () => {
    setConfirmedLoading(true);
    // notReleased — exclude proformas that already own a work order. A released
    // order keeps its CONFIRMED status, so without this the picker offered
    // already-released proformas that then 409 (ALREADY_RELEASED) on submit.
    try { setConfirmed(normList(await proformaApi.list({ status: 'CONFIRMED', notReleased: true, limit: 200 }))); }
    catch (_e) { setConfirmed([]); }
    finally { setConfirmedLoading(false); }
  }, []);
  const openDrawer = () => {
    form.resetFields();
    form.setFieldsValue({ siteCode: '1' });
    setDrawerOpen(true);
    loadConfirmed();
  };
  const closeDrawer = () => { setDrawerOpen(false); setConfirmed([]); };

  const proformaOptions = useMemo(
    () => confirmed.map((p) => {
      const no = p.proformaNo || p.proforma_no || p.id;
      const cust = p.customerName || p.customer_name || '';
      const total = p.grandTotal ?? p.grand_total;
      return { value: p.id, label: [no, cust, total != null ? money(total) : null].filter(Boolean).join(' · ') };
    }),
    [confirmed]
  );

  const submitRelease = async () => {
    let values;
    try { values = await form.validateFields(); } catch (_e) { return; }
    setReleasing(true);
    try {
      const out = await workOrdersApi.release({
        proformaId: values.proformaId,
        siteCode: values.siteCode || undefined,
        expectedDeliveryDate: values.expectedDeliveryDate ? values.expectedDeliveryDate.format('YYYY-MM-DD') : undefined
      });
      message.success(`${out.woNo} released — ${out.pieceCount} piece${out.pieceCount === 1 ? '' : 's'} tagged with barcodes.`);
      closeDrawer();
      refresh();
      if (out.workOrderId) navigate(`/erp/production/work-orders/${out.workOrderId}`);
    } catch (err) {
      message.error(backendError(err, 'Could not release the proforma.'));
    } finally { setReleasing(false); }
  };

  const openDetail = useCallback((row) => {
    if (row && row.id) navigate(`/erp/production/work-orders/${row.id}`);
  }, [navigate]);

  // Production Stages modal (legacy per-order updation popup, Glass-i theme).
  const [stagesRow, setStagesRow] = useState(null);
  const openStages = useCallback((row) => { if (row && row.id) setStagesRow(row); }, []);

  // A stage cell is { done, routed } (piece counts). Shows "done/routed"; greys
  // when the stage isn't in the route, and greens when every routed piece is done.
  const deptCell = (cell) => {
    const c = (cell && typeof cell === 'object') ? cell : { done: 0, routed: Number(cell) || 0 };
    const routed = Number(c.routed) || 0;
    const done = Number(c.done) || 0;
    if (routed === 0) return <span className="erp-wo-dept erp-wo-dept--zero">—</span>;
    const full = done >= routed;
    return (
      <span className="erp-wo-dept tabular-nums" style={full ? { color: '#16a34a', fontWeight: 600 } : undefined}>
        {done}/{routed}
      </span>
    );
  };

  const columns = useMemo(() => [
    { title: 'S.NO', key: '__sno__', width: 56, align: 'center',
      render: (_v, _r, idx) => <span className="erp-grid__muted">{idx + 1}</span> },
    { title: 'DAYS', dataIndex: 'days', key: 'days', width: 64, align: 'center',
      sorter: (a, b) => (Number(a.days) || 0) - (Number(b.days) || 0),
      render: (v, r) => {
        const n = Number(v) || 0;
        return <span className="tabular-nums" style={isCritical(n, r.status) ? { color: '#dc2626', fontWeight: 600 } : undefined}>{n}</span>;
      } },
    { title: 'WONO', dataIndex: 'woNo', key: 'woNo', width: 170, fixed: 'left',
      sorter: (a, b) => String(a.woNo || '').localeCompare(String(b.woNo || '')),
      render: (v, r) => v
        ? <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontWeight: 600 }} onClick={() => openDetail(r)}>{v}</Button>
        : null },
    { title: 'WO DATE', dataIndex: 'woDate', key: 'woDate', width: 108, dataType: 'date',
      sorter: (a, b) => String(a.woDate || '').localeCompare(String(b.woDate || '')),
      render: fmtDate },
    { title: 'PI NO', dataIndex: 'proformaNo', key: 'proformaNo', width: 150,
      render: (v, r) => v
        ? (r.proformaId
          ? <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/erp/sales/proformas/${r.proformaId}`)}>{v}</Button>
          : <code className="erp-grid__code">{v}</code>)
        : null },
    { title: 'CUSTOMER', dataIndex: 'customerName', key: 'customerName', width: 220, ellipsis: true },
    { title: 'THICK', dataIndex: 'thick', key: 'thick', width: 84, align: 'center',
      render: (v) => v || <span className="erp-grid__muted">—</span> },
    { title: 'TYPE', dataIndex: 'type', key: 'type', width: 120,
      render: (v) => v ? <Tag color="blue" className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{v}</Tag> : null },
    { title: 'TOTAL', key: 'total', children: [
      { title: 'PCS', dataIndex: 'pcs', key: 'pcs', width: 64, align: 'right',
        sorter: (a, b) => (Number(a.pcs) || 0) - (Number(b.pcs) || 0),
        render: (v) => <span className="tabular-nums">{Number(v) || 0}</span> },
      { title: 'SQM', dataIndex: 'sqm', key: 'sqm', width: 72, align: 'right',
        sorter: (a, b) => (Number(a.sqm) || 0) - (Number(b.sqm) || 0),
        render: (v) => <span className="tabular-nums">{formatSqm(v)}</span> }
    ] },
    { title: 'PRODUCTION', key: 'production',
      children: stageColumns.map((c) => ({
        title: <Tooltip title={`${c.name} — completed / routed pieces`}>{c.letter}</Tooltip>,
        key: c.code, width: 58, align: 'center',
        render: (_v, row) => deptCell(row.stages && row.stages[c.code])
      })) },
    { title: '%', dataIndex: 'pct', key: 'pct', width: 56, align: 'center',
      sorter: (a, b) => (Number(a.pct) || 0) - (Number(b.pct) || 0),
      render: (v) => {
        const n = Number(v) || 0;
        return <span className="tabular-nums" style={{ color: n === 100 ? '#16a34a' : '#64748b', fontWeight: n === 100 ? 600 : 400 }}>{n}%</span>;
      } },
    { title: 'STATUS', dataIndex: 'status', key: 'status', width: 128, align: 'center', dataType: 'enum',
      render: (v) => <Tag color={woStatusColor(v)} className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{(woStatusLabel(v) || '').toUpperCase()}</Tag> },
    { title: '', key: 'actions', width: 84, align: 'center', emptyFallback: false,
      render: (_v, r) => (
        <Space size={0}>
          <Tooltip title="Production stages">
            <Button size="small" type="text" className="erp-wo-stages-btn" icon={<AppstoreOutlined />} onClick={() => openStages(r)} />
          </Tooltip>
          <Tooltip title="Open work order">
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => openDetail(r)} />
          </Tooltip>
        </Space>
      ) }
  ], [openDetail, openStages, navigate, stageColumns]);

  const contextMenuActions = useMemo(() => ([
    { key: 'stages', label: 'Production stages', icon: <AppstoreOutlined />, onClick: openStages },
    { key: 'open', label: 'Open work order', icon: <EyeOutlined />, onClick: openDetail }
  ]), [openStages, openDetail]);

  const statusOptions = useMemo(() => WO_STATUS_TABS.map((t) => ({ value: t.key, label: t.label })), []);
  const pageSizeOptions = useMemo(() => ['25', '50', '100', '200'], []);
  const actionsProp = useMemo(() => ({ add: mayRelease }), [mayRelease]);

  const toolbarExtra = useMemo(() => (
    <Space size="small" wrap>
      <Input allowClear size="small" style={{ width: 180 }} placeholder="Customer…" value={customer} onChange={(e) => setCustomer(e.target.value)} />
      <Input allowClear size="small" style={{ width: 150 }} placeholder="WONO…" value={wono} onChange={(e) => setWono(e.target.value)} />
      <Input allowClear size="small" style={{ width: 150 }} placeholder="PI No…" value={pino} onChange={(e) => setPino(e.target.value)} />
      <span className="erp-wo-filter-label">Status:</span>
      <Select size="small" style={{ width: 150 }} value={tab} onChange={setTab} options={statusOptions} />
    </Space>
  ), [customer, wono, pino, tab, statusOptions]);

  return (
    <ErpMasterShell>
      <div className="erp-production-page">
        {!mayView ? (
          <Alert type="warning" showIcon className="mb-3" message="You don't have permission to view work orders." />
        ) : null}

        <ErpDataGrid
          {...gridHandlers}
          title="Order Tracking"
          className="erp-wo-tracking"
          gridId="production-order-tracking-v1"
          columns={columns}
          rowKey="id"
          loading={loading}
          rowHeight="compact"
          stickyHeader
          pageSizeOptions={pageSizeOptions}
          searchable={false}
          exportable
          exportFilename="order-tracking"
          actions={actionsProp}
          addButtonText="Release to Production"
          onAdd={openDrawer}
          contextMenuActions={contextMenuActions}
          toolbarExtra={toolbarExtra}
          emptyTitle="No work orders yet"
          emptyText={mayRelease ? 'Click Release to Production to turn a confirmed proforma into a piece-tracked work order.' : 'No work orders to display.'}
          emptyCtaLabel={mayRelease ? 'Release to Production' : undefined}
        />
      </div>

      {/* Release — pick a CONFIRMED proforma (1:1) + the production site digit. */}
      <Drawer
        title="Release to Production"
        open={drawerOpen}
        onClose={closeDrawer}
        width={480}
        destroyOnHidden
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" icon={<RocketOutlined />} loading={releasing} onClick={submitRelease}>Release</Button>
          </div>
        }
      >
        <Alert type="info" showIcon className="mb-3"
          message="Releasing snapshots the proforma's lines, derives each line's production route, and pre-assigns a unique barcode to every piece. One work order per proforma."
        />
        <Form form={form} layout="vertical" requiredMark="optional" className="erp-production-form">
          <Form.Item name="proformaId" label="Confirmed proforma" rules={[{ required: true, message: 'Pick a confirmed proforma.' }]}>
            <Select
              showSearch optionFilterProp="label"
              loading={confirmedLoading}
              placeholder="Select a confirmed proforma"
              options={proformaOptions}
              notFoundContent={confirmedLoading ? 'Loading…' : 'No confirmed proformas available'}
            />
          </Form.Item>
          <Form.Item name="expectedDeliveryDate" label="Expected delivery date"
            extra="The delivery commitment for this order — drives dispatch planning."
            rules={[{ required: true, message: 'Set the expected delivery date.' }]}>
            <DatePicker format="DD-MMM-YYYY" style={{ width: '100%' }} placeholder="Select delivery date" />
          </Form.Item>
          <Form.Item name="siteCode" label="Production site"
            extra="A single digit (0–9) that prefixes every piece barcode for this site."
            rules={[
              { required: true, message: 'Enter the site digit.' },
              { pattern: /^[0-9]$/, message: 'Site must be a single digit 0–9.' }
            ]}>
            <Input maxLength={1} style={{ width: 96 }} placeholder="1" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Production Stages — legacy per-order updation popup, Glass-i theme (read-only). */}
      <WorkOrderStagesModal
        open={!!stagesRow}
        row={stagesRow}
        stageColumns={stageColumns}
        onClose={() => setStagesRow(null)}
        onOpenDetail={(r) => { setStagesRow(null); openDetail(r); }}
      />
    </ErpMasterShell>
  );
}
